import { useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import dayjs from 'dayjs';

import PaymentsOutlined from '@mui/icons-material/PaymentsOutlined';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import AccountBalanceWalletOutlined from '@mui/icons-material/AccountBalanceWalletOutlined';
import LocalShippingOutlined from '@mui/icons-material/LocalShippingOutlined';
import LocalOfferOutlined from '@mui/icons-material/LocalOfferOutlined';
import PercentOutlined from '@mui/icons-material/PercentOutlined';
import TrendingUpOutlined from '@mui/icons-material/TrendingUpOutlined';

import useRevenueReport from 'hooks/reports/useRevenueReport';
import ReportHeader from 'components/reports/ReportHeader';
import ReportFilters from 'components/reports/ReportFilters';
import ReportSummaryCards from 'components/reports/ReportSummaryCards';
import ReportTable from 'components/reports/ReportTable';
import { RevenueTrendChart, ReportBarChart } from 'components/reports/ReportCharts';
import ExportActions from 'components/reports/ExportActions';
import EmptyState from 'components/reports/EmptyState';
import LoadingState from 'components/reports/LoadingState';

import { useCurrency } from 'contexts/CurrencyContext';
import { formatCurrency } from 'utils/currency';
import CurrencyAmount from 'components/currency/CurrencyAmount';
import { orderStatusChipSx, normalizeOrderStatusKey } from 'components/table/tableChipStyles';

const getErrorMessage = (error) => {
  if (!error) return '';
  const data = error.response?.data;
  if (data) {
    if (Array.isArray(data.errors) && data.errors.length > 0) {
      return `${data.message}: ${data.errors.join(', ')}`;
    }
    return data.message || data.error || error.message;
  }
  return error.message;
};

export default function RevenueReport() {
  const { currency } = useCurrency();
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse URL search params
  const startDate = searchParams.get('startDate') || dayjs().subtract(30, 'day').format('YYYY-MM-DD');
  const endDate = searchParams.get('endDate') || dayjs().format('YYYY-MM-DD');
  const storeId = searchParams.get('storeId') || '';
  const search = searchParams.get('search') || '';
  const status = searchParams.get('status') || '';
  const page = parseInt(searchParams.get('page') || '1', 10);
  const sortBy = searchParams.get('sortBy') || 'timestamp';
  const sortOrder = searchParams.get('sortOrder') || 'DESC';

  const filters = { startDate, endDate, storeId, search, status, page, sortBy, sortOrder };

  // Fetch report data via React Query
  const { data, isLoading, error, refetch } = useRevenueReport({
    startDate,
    endDate,
    storeId,
    search,
    page,
    limit: 10,
    sortBy,
    sortOrder
  });

  const handleFilterChange = (newFilters) => {
    const nextParams = {};
    Object.keys(newFilters).forEach((key) => {
      if (newFilters[key] !== undefined && newFilters[key] !== '') {
        nextParams[key] = String(newFilters[key]);
      }
    });
    setSearchParams(nextParams);
  };

  const handlePaginationModelChange = (model) => {
    handleFilterChange({
      ...filters,
      page: model.page + 1
    });
  };

  const handleSortModelChange = (sortModel) => {
    if (sortModel.length > 0) {
      const { field, sort } = sortModel[0];
      handleFilterChange({
        ...filters,
        sortBy: field,
        sortOrder: sort.toUpperCase(),
        page: 1
      });
    } else {
      handleFilterChange({
        ...filters,
        sortBy: 'timestamp',
        sortOrder: 'DESC',
        page: 1
      });
    }
  };

  const responseData = data?.data || {};
  const summary = responseData.summary || {};
  const listing = responseData.listing || [];
  const pagination = responseData.pagination || {};

  // Client-side filtering of listing based on status dropdown
  const filteredListing = status ? listing.filter((o) => String(o.order_status).toLowerCase() === status.toLowerCase()) : listing;

  // Generate Daily Trend data from listing
  const getTrendData = () => {
    const sorted = [...listing].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    const dailyMap = {};
    sorted.forEach((o) => {
      const dStr = new Date(o.timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
      if (!dailyMap[dStr]) {
        dailyMap[dStr] = { date: dStr, revenue: 0 };
      }
      dailyMap[dStr].revenue += o.grand_total;
    });
    return Object.values(dailyMap);
  };

  // Generate Monthly Trend data from listing
  const getMonthlyData = () => {
    const sorted = [...listing].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    const monthlyMap = {};
    sorted.forEach((o) => {
      const date = new Date(o.timestamp);
      const mStr = date.toLocaleDateString(undefined, { year: 'numeric', month: 'short' });
      if (!monthlyMap[mStr]) {
        monthlyMap[mStr] = { month: mStr, revenue: 0 };
      }
      monthlyMap[mStr].revenue += o.grand_total;
    });
    return Object.values(monthlyMap).map((m) => ({ date: m.month, revenue: m.revenue }));
  };

  const trendData = getTrendData();
  const monthlyData = getMonthlyData();

  // Summary Cards Configuration
  const summaryCards = [
    {
      label: 'Total Revenue',
      value: <CurrencyAmount value={summary.total_revenue || 0} variant="h3" color="inherit" fontWeight={800} />,
      tooltip: 'Sum of grand total of all orders (excludes Cancelled/Rejected)',
      icon: PaymentsOutlined,
      color: '#1f5b40'
    },
    {
      label: 'Total Orders',
      value: Number(summary.total_orders || 0).toLocaleString(),
      tooltip: 'Total order count (excludes Cancelled/Rejected)',
      icon: ReceiptLongOutlined,
      color: '#359163'
    },
    {
      label: 'Sub Total',
      value: <CurrencyAmount value={summary.total_subtotal || 0} variant="h3" color="inherit" fontWeight={800} />,
      tooltip: 'Sum of subtotal (items only, excludes discounts, taxes, and delivery charges)',
      icon: AccountBalanceWalletOutlined,
      color: '#133a28'
    },
    {
      label: 'Delivery Charges',
      value: <CurrencyAmount value={summary.total_delivery_charge || 0} variant="h3" color="inherit" fontWeight={800} />,
      tooltip: 'Sum of delivery fees collected',
      icon: LocalShippingOutlined,
      color: '#2b7a78'
    },
    {
      label: 'Discounts',
      value: <CurrencyAmount value={summary.total_discount || 0} variant="h3" color="inherit" fontWeight={800} />,
      tooltip: 'Sum of promotional discount deductions',
      icon: LocalOfferOutlined,
      color: '#b71c1c'
    },
    {
      label: 'Tax Collected',
      value: <CurrencyAmount value={summary.total_tax || 0} variant="h3" color="inherit" fontWeight={800} />,
      tooltip: 'Sum of VAT/tax collected',
      icon: PercentOutlined,
      color: '#d4920a'
    },
    {
      label: 'Average Order Value',
      value: <CurrencyAmount value={summary.average_order_value || 0} variant="h3" color="inherit" fontWeight={800} />,
      tooltip: 'Revenue divided by order count',
      icon: TrendingUpOutlined,
      color: '#a94442'
    }
  ];

  // Table Columns
  const tableColumns = [
    { field: 'order_no', headerName: 'Order No', flex: 1, minWidth: 120, sortable: false },
    {
      field: 'timestamp',
      headerName: 'Date',
      flex: 1.2,
      minWidth: 150,
      valueFormatter: (params) => (params.value ? new Date(params.value).toLocaleDateString() : '')
    },
    {
      field: 'Customer',
      headerName: 'Customer',
      flex: 1.3,
      minWidth: 160,
      sortable: false,
      valueGetter: (value, row) => row.Customer?.name || '—'
    },
    {
      field: 'Store',
      headerName: 'Store',
      flex: 1.3,
      minWidth: 160,
      sortable: false,
      valueGetter: (value, row) => row.Store?.title_en || '—'
    },
    {
      field: 'order_status',
      headerName: 'Status',
      flex: 1.2,
      minWidth: 130,
      renderCell: (params) => <Chip label={params.value} size="small" sx={orderStatusChipSx(normalizeOrderStatusKey(params.value))} />
    },
    {
      field: 'sub_total',
      headerName: 'Subtotal',
      flex: 1,
      minWidth: 110,
      renderCell: (params) => <CurrencyAmount value={params.value || 0} />,
      valueFormatter: (params) => formatCurrency(params.value || 0, currency.code, currency.symbol)
    },
    {
      field: 'tax_total',
      headerName: 'Tax',
      flex: 1,
      minWidth: 100,
      renderCell: (params) => <CurrencyAmount value={params.value || 0} />,
      valueFormatter: (params) => formatCurrency(params.value || 0, currency.code, currency.symbol)
    },
    {
      field: 'grand_total',
      headerName: 'Grand Total',
      flex: 1.1,
      minWidth: 120,
      renderCell: (params) => <CurrencyAmount value={params.value || 0} fontWeight={700} />,
      valueFormatter: (params) => formatCurrency(params.value || 0, currency.code, currency.symbol)
    }
  ];

  const paginationModel = {
    page: page - 1,
    pageSize: 10
  };

  return (
    <Box sx={{ pb: 3 }}>
      <ReportHeader
        title="Revenue Report"
        subtitle="Analyze sales statistics, deliveries, discounts, tax collections, and store earnings."
        actions={<ExportActions data={filteredListing} columns={tableColumns} title="Revenue Report" />}
      />

      <ReportFilters filters={filters} onFilterChange={handleFilterChange} />

      {isLoading ? (
        <LoadingState cardCount={7} />
      ) : error ? (
        <EmptyState type="error" message={getErrorMessage(error)} onRetry={refetch} />
      ) : (
        <Box sx={{ animation: 'fadeIn 0.3s ease-out' }}>
          <ReportSummaryCards cards={summaryCards} />

          {listing.length > 0 && (
            <Grid container spacing={2.5} sx={{ mb: 3.5 }}>
              <Grid size={{ xs: 12, lg: 6 }}>
                <RevenueTrendChart data={trendData} title="Revenue Trend" />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                <ReportBarChart data={trendData} xKey="date" yKey="revenue" name="Revenue" title="Daily Revenue" isCurrency />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
                <ReportBarChart data={monthlyData} xKey="date" yKey="revenue" name="Revenue" title="Monthly Revenue" isCurrency />
              </Grid>
            </Grid>
          )}

          {filteredListing.length === 0 ? (
            <EmptyState
              type={search || storeId || status ? 'nofilters' : 'nodata'}
              message={search || storeId || status ? undefined : 'No report data available'}
            />
          ) : (
            <ReportTable
              columns={tableColumns}
              rows={filteredListing}
              loading={isLoading}
              rowCount={pagination.total || 0}
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationModelChange}
              onSortModelChange={handleSortModelChange}
            />
          )}
        </Box>
      )}
    </Box>
  );
}
